import React, { useMemo, useState } from "react";

import Layout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Tabs from "src/components/ui/Tabs";
import Card from "src/components/common/card/Card";
import SalesOverviewChart from "src/components/charts/SalesOverviewChart";
import PurchaseOverviewChart from "src/components/charts/PurchaseOverviewChart";
import TopProductsChart from "src/components/charts/TopProductsChart";

const AdminReportsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState("overview");

  const tabs = useMemo(
    () => [
      { label: "Overview", value: "overview" },
      { label: "Revenue", value: "revenue" },
      { label: "Tenants", value: "tenants" },
      { label: "Operations", value: "operations" },
    ],
    []
  );

  const revenueSeries = useMemo(
    () => [
      { date: "Mon", totalSales: 42000 },
      { date: "Tue", totalSales: 39000 },
      { date: "Wed", totalSales: 51000 },
      { date: "Thu", totalSales: 47000 },
      { date: "Fri", totalSales: 56000 },
      { date: "Sat", totalSales: 62000 },
      { date: "Sun", totalSales: 45000 },
    ],
    []
  );

  const billingSeries = useMemo(
    () => [
      { date: "Mon", totalPurchases: 32000 },
      { date: "Tue", totalPurchases: 36000 },
      { date: "Wed", totalPurchases: 30000 },
      { date: "Thu", totalPurchases: 41000 },
      { date: "Fri", totalPurchases: 38000 },
      { date: "Sat", totalPurchases: 42000 },
      { date: "Sun", totalPurchases: 35000 },
    ],
    []
  );

  const topIndustries = useMemo(
    () => [
      { name: "Retail", totalSales: 320000 },
      { name: "Wholesale", totalSales: 240000 },
      { name: "Manufacturing", totalSales: 180000 },
      { name: "E-commerce", totalSales: 140000 },
    ],
    []
  );

  return (
    <Layout
      type="admin"
      meta={{
        title: "Reports | Invyntra",
        description: "Monitor platform-wide revenue, tenants, and operations.",
        keywords: "reports, admin, analytics, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Reports"
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Reports", path: "/admin/reports" },
          ]}
        />

        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
          className="flex flex-wrap gap-2 border-b"
        />

        {activeTab === "overview" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {[
                { label: "Active Tenants", value: "214" },
                { label: "Monthly Recurring Revenue", value: "INR 18.6L" },
                { label: "Avg. Plan Utilization", value: "72%" },
                { label: "Support Requests (30d)", value: "126" },
              ].map((item) => (
                <Card key={item.label}>
                  <p className="text-sm text-gray-500">{item.label}</p>
                  <p className="text-2xl font-semibold text-gray-800 mt-2">
                    {item.value}
                  </p>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <p className="text-sm text-gray-600 font-medium">
                  Revenue Trend
                </p>
                <SalesOverviewChart data={revenueSeries} />
              </Card>
              <Card>
                <p className="text-sm text-gray-600 font-medium">
                  Billing Volume
                </p>
                <PurchaseOverviewChart data={billingSeries} />
              </Card>
            </div>
          </div>
        )}

        {activeTab === "revenue" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <p className="text-sm text-gray-600 font-medium">
                Weekly Revenue
              </p>
              <SalesOverviewChart data={revenueSeries} />
            </Card>
            <Card>
              <p className="text-sm text-gray-600 font-medium">
                Top Revenue Industries
              </p>
              <TopProductsChart data={topIndustries} />
            </Card>
          </div>
        )}

        {activeTab === "tenants" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <p className="text-sm text-gray-600 font-medium">
                High Utilization Tenants
              </p>
              <ul className="mt-4 space-y-3 text-sm text-gray-700">
                {[
                  "Greenleaf Stores - 92% plan usage",
                  "Swift Retail - 88% plan usage",
                  "Nova Wholesale - 86% plan usage",
                ].map((line) => (
                  <li key={line} className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-400" />
                    {line}
                  </li>
                ))}
              </ul>
            </Card>
            <Card>
              <p className="text-sm text-gray-600 font-medium">
                Plan Distribution
              </p>
              <ul className="mt-4 space-y-3 text-sm text-gray-700">
                {[
                  "Starter: 108 tenants",
                  "Business: 74 tenants",
                  "Enterprise: 32 tenants",
                ].map((line) => (
                  <li key={line} className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-indigo-400" />
                    {line}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        )}

        {activeTab === "operations" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <p className="text-sm text-gray-600 font-medium">
                Support Resolution
              </p>
              <div className="mt-4 space-y-3 text-sm text-gray-700">
                <div className="flex items-center justify-between">
                  <span>Avg. first response</span>
                  <span className="font-semibold">2h 14m</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Avg. resolution</span>
                  <span className="font-semibold">18h 20m</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Open requests</span>
                  <span className="font-semibold">42</span>
                </div>
              </div>
            </Card>
            <Card>
              <p className="text-sm text-gray-600 font-medium">
                Platform Health
              </p>
              <div className="mt-4 space-y-3 text-sm text-gray-700">
                <div className="flex items-center justify-between">
                  <span>API uptime (30d)</span>
                  <span className="font-semibold">99.8%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Failed syncs</span>
                  <span className="font-semibold">8</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Average API latency</span>
                  <span className="font-semibold">220ms</span>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminReportsPage;
